import { Controller } from "@hotwired/stimulus"
import axios from "axios"
import Bugsnag from "../bugsnag"
import { GalleryLightbox, type GalleryImage } from "../utils/gallery_lightbox"
import { GalleryThumbnails } from "../utils/gallery_thumbnails"

/**
 * Gallery Controller
 *
 * A unified image gallery using PhotoSwipe (lightbox) + Swiper (thumbnails).
 *
 * Features:
 * - Touch/swipe navigation on mobile
 * - Keyboard navigation (arrows, escape)
 * - Pinch-to-zoom, double-tap zoom, wheel zoom
 * - Print current image
 * - Synced thumbnail strip
 *
 * Usage:
 * Add data-controller="gallery" to a container with:
 * - data-gallery-images-url-value: URL to fetch images JSON
 * - data-action="click->gallery#open" on thumbnail triggers
 * - data-gallery-index on triggers to specify starting image
 */
export default class GalleryController extends Controller {
  static values = {
    imagesUrl: String,
  }

  declare imagesUrlValue: string
  declare hasImagesUrlValue: boolean

  private lightbox: GalleryLightbox | null = null
  private thumbnails: GalleryThumbnails | null = null
  private images: GalleryImage[] = []

  // Track current scope to detect when we need to reload
  private currentScope: string | null = null

  /**
   * Open the gallery at a specific index.
   * Called from thumbnail click events.
   */
  async open(event: Event) {
    event.preventDefault()

    const trigger = event.currentTarget as HTMLElement
    const attachmentId = trigger.dataset.attachmentId
    const recordType = trigger.dataset.galleryRecordType
    const recordId = trigger.dataset.galleryRecordId

    // Determine scope key (null for full gallery, "Type:ID" for scoped)
    const scopeKey = recordType && recordId ? `${recordType}:${recordId}` : null

    // Reload images if scope changed or not yet loaded
    const needsReload = this.images.length === 0 || this.currentScope !== scopeKey
    if (needsReload && this.hasImagesUrlValue) {
      await this.loadImages(recordType, recordId)
      this.currentScope = scopeKey
    }

    if (this.images.length === 0) return

    // Find index by attachment ID, fall back to 0 if not found
    let index = 0
    if (attachmentId) {
      const foundIndex = this.images.findIndex(
        (img) => img.id === parseInt(attachmentId, 10)
      )
      if (foundIndex >= 0) {
        index = foundIndex
      }
    }

    this.initLightbox(index)
  }

  private async loadImages(recordType?: string, recordId?: string) {
    try {
      let url: string

      if (recordType && recordId) {
        // Use RESTful member route: /messages/:id/images or /responses/:id/images
        const resourcePath = recordType === "Message" ? "messages" : "responses"
        url = `/${resourcePath}/${recordId}/images`
      } else {
        // Fall back to full conversation gallery
        url = this.imagesUrlValue
      }

      const response = await axios.get<GalleryImage[]>(url)
      this.images = response.data
    } catch (error) {
      Bugsnag.notify(new Error("Failed to load gallery images", { cause: error }))
      this.images = []
    }
  }

  /**
   * Initialize the lightbox with thumbnail strip.
   */
  private initLightbox(startIndex: number) {
    this.lightbox = new GalleryLightbox(this.images, {
      onSlideChange: (index) => {
        this.thumbnails?.syncTo(index)
      },
      onDestroy: () => {
        this.thumbnails?.destroy()
        this.thumbnails = null
        this.lightbox = null
      },
      onUiRegister: (pswp) => {
        // Register thumbnail strip (only if more than 1 image)
        if (this.images.length > 1) {
          pswp.ui?.registerElement({
            name: "thumbnail-strip",
            className: "pswp__thumbnail-strip",
            appendTo: "wrapper",
            onInit: (el) => {
              this.thumbnails = new GalleryThumbnails(el, this.images, (index) => {
                this.lightbox?.goTo(index)
              })
              this.thumbnails.syncTo(this.lightbox?.getCurrentIndex() ?? 0)
            },
          })
        }
      },
    })

    this.lightbox.open(startIndex)
  }

  /**
   * Clean up when controller disconnects.
   */
  disconnect() {
    this.lightbox?.destroy()
    this.thumbnails?.destroy()
    this.lightbox = null
    this.thumbnails = null
    this.images = []
  }
}
